import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Alert,
  AlertTitle,
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Switch,
} from "@mui/material";
import { ANDatePicker } from "@answersnow/pkg-theme";
import { X } from "@phosphor-icons/react";
import AddIcon from "@mui/icons-material/Add";

interface NewAuthorizationDialogProps {
  open: boolean;
  newAuthorizationCode: string;
  newAuthorizationStartDate: Date | null;
  newAuthorizationEndDate: Date | null;
  newAuthorizationType: string;
  newAuthorizationSubtype: string;
  newAuthorizationClinicianId: string;
  newPendingAuth: boolean;
  newApprovedUnits: any[];
  clinicianList: any[];
  availableInsuranceCodes: any[];
  authorizationCodes: any[];
  silnaAssessmentAuthStatus: string;
  onChange: (name: string) => (e: any) => void;
  onChangeDate: (name: string, date: Date | null) => void;
  onToggleNewAuthorization: () => void;
  saveNewAuthorization: () => void;
  onAddApprovedUnit: () => void;
  onRemoveApprovedUnit: (index: number) => void;
  onChangeApprovedUnitCode: (index: number, e: any) => void;
  onChangeApprovedUnitAmount: (index: number, e: any) => void;
}

const NewAuthorizationDialog: React.FC<NewAuthorizationDialogProps> = ({
  open,
  newAuthorizationCode,
  newAuthorizationStartDate,
  newAuthorizationEndDate,
  newAuthorizationType,
  newAuthorizationSubtype,
  newAuthorizationClinicianId,
  newPendingAuth,
  newApprovedUnits,
  clinicianList,
  availableInsuranceCodes,
  silnaAssessmentAuthStatus,
  onChange,
  onChangeDate,
  onToggleNewAuthorization,
  saveNewAuthorization,
  onAddApprovedUnit,
  onRemoveApprovedUnit,
  onChangeApprovedUnitCode,
  onChangeApprovedUnitAmount,
}) => {
  const [sortedClinicians, setSortedClinicians] = useState<any[]>([]);

  useEffect(() => {
    if (clinicianList) {
      const sortedList = [...clinicianList].sort((a, b) =>
        a.first_name.localeCompare(b.first_name)
      );
      setSortedClinicians(sortedList);
    }
  }, [clinicianList]);

  const newApprovedUnitsHaveMissingInformation = newApprovedUnits?.some(
    (unit) => !unit.code || !unit.units || !unit.hours || !unit.codeId
  );

  const saveAuthDisabled =
    (!newAuthorizationCode && !newPendingAuth) ||
    !newAuthorizationStartDate ||
    !newAuthorizationEndDate ||
    !newAuthorizationType ||
    (newAuthorizationType === "secondary_auth" && !newAuthorizationSubtype) ||
    (newAuthorizationType !== "secondary_auth" &&
      (!newAuthorizationClinicianId ||
        newApprovedUnits.length === 0 ||
        newApprovedUnitsHaveMissingInformation));

  const authCodes =
    newAuthorizationType === "re_auth"
      ? availableInsuranceCodes.filter((code: any) => !!code.re_auth)
      : availableInsuranceCodes;

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={onToggleNewAuthorization}
      aria-labelledby="new-authorization-title"
    >
      <DialogTitle id="new-authorization-title">
        Add Authorization Period
        <IconButton
          aria-label="close"
          onClick={onToggleNewAuthorization}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <X />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ zIndex: 2010 }}>
        <Grid container justifyContent="space-between" spacing={2} sx={{ mt: 1 }}>
          {newAuthorizationType !== "secondary_auth" && (
            <>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Authorization type"
                  id="auth-type"
                  onChange={onChange("newAuthorizationType")}
                  value={newAuthorizationType}
                  fullWidth
                >
                  <MenuItem disabled value="">
                    Select Type
                  </MenuItem>
                  <MenuItem
                    value="pre_auth"
                    disabled={
                      silnaAssessmentAuthStatus === "PENDING" ||
                      silnaAssessmentAuthStatus === "ACTION_REQUIRED"
                    }
                  >
                    Pre-Authorization
                    {(silnaAssessmentAuthStatus === "PENDING" ||
                      silnaAssessmentAuthStatus === "ACTION_REQUIRED") && (
                      <Typography sx={{ fontSize: "12px" }}>
                        (Pending Authorization Submitted)
                      </Typography>
                    )}
                  </MenuItem>
                  <MenuItem value="auth">Ongoing Authorization</MenuItem>
                  <MenuItem value="secondary_auth">Secondary Authorization</MenuItem>
                  <MenuItem value="re_auth">Re-Authorization</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Approved clinician"
                  id="auth-clinician"
                  onChange={onChange("newAuthorizationClinicianId")}
                  value={newAuthorizationClinicianId}
                  fullWidth
                  disabled={!newAuthorizationType}
                >
                  <MenuItem disabled value="">
                    Select clinician
                  </MenuItem>
                  {sortedClinicians?.map((clinician: any) => (
                    <MenuItem key={clinician.clinician_id} value={clinician.clinician_id}>
                      {clinician.first_name} {clinician.last_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} my={7}>
                <Alert
                  action={
                    <Switch
                      checked={newPendingAuth}
                      onChange={onChange("newPendingAuth")}
                      inputProps={{
                        "aria-label": "Temporary Authorization and Pending Credentials",
                      }}
                    />
                  }
                  severity="info"
                >
                  <AlertTitle>Temporary Authorization and Pending Credentials</AlertTitle>
                  Turning on Temporary Authorization and Pending Credentials allows AnswersNow to
                  see the client while authorizations and BCBA credentials are pending approvals
                </Alert>
              </Grid>
              <Grid item xs={4}>
                <ANDatePicker
                  label="Start date"
                  value={newAuthorizationStartDate ? new Date(newAuthorizationStartDate) : null}
                  onChange={(date: Date | null) => onChangeDate("newAuthorizationStartDate", date)}
                  placeholder="Pick a date"
                  format="PP"
                  disabled={!newAuthorizationType}
                  slotProps={{
                    field: { fullWidth: true },
                    popper: { disablePortal: false, style: { zIndex: 4000 } },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ANDatePicker
                  label="End date"
                  value={newAuthorizationEndDate ? new Date(newAuthorizationEndDate) : null}
                  onChange={(date: Date | null) => onChangeDate("newAuthorizationEndDate", date)}
                  placeholder="Pick a date"
                  format="PP"
                  disabled={!newAuthorizationType}
                  slotProps={{
                    field: { fullWidth: true },
                    popper: { disablePortal: false, style: { zIndex: 4000 } },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Authorization number"
                  id="authorizationCode"
                  placeholder="Enter authorization code"
                  onChange={onChange("newAuthorizationCode")}
                  value={newAuthorizationCode}
                  variant="outlined"
                  fullWidth
                  disabled={!newAuthorizationType || newPendingAuth}
                />
              </Grid>
            </>
          )}

          {newAuthorizationType === "secondary_auth" && (
            <>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Authorization type"
                  id="auth-type"
                  onChange={onChange("newAuthorizationType")}
                  value={newAuthorizationType}
                  fullWidth
                >
                  <MenuItem disabled value="">
                    Select Type
                  </MenuItem>
                  <MenuItem value="pre_auth">Pre-Authorization</MenuItem>
                  <MenuItem value="auth">Ongoing Authorization</MenuItem>
                  <MenuItem value="secondary_auth">Secondary Authorization</MenuItem>
                  <MenuItem value="re_auth">Re-Authorization</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Authorization number"
                  id="authorizationCode"
                  placeholder="Enter authorization code"
                  onChange={onChange("newAuthorizationCode")}
                  value={newAuthorizationCode}
                  variant="outlined"
                  fullWidth
                  disabled={!newAuthorizationType || newPendingAuth}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginTop: "24px" }}>
                <TextField
                  select
                  label="Authorization subtype"
                  id="auth-subtype"
                  onChange={onChange("newAuthorizationSubtype")}
                  value={newAuthorizationSubtype}
                  fullWidth
                >
                  <MenuItem disabled value="">
                    Select Type
                  </MenuItem>
                  <MenuItem value="pre_auth">Pre-Authorization</MenuItem>
                  <MenuItem value="auth">Ongoing Authorization</MenuItem>
                  <MenuItem value="re_auth">Re-Authorization</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} my={7}>
                <Alert
                  action={
                    <Switch
                      checked={newPendingAuth}
                      onChange={onChange("newPendingAuth")}
                      inputProps={{
                        "aria-label": "Temporary Authorization and Pending Credentials",
                      }}
                    />
                  }
                  severity="info"
                >
                  <AlertTitle>Temporary Authorization and Pending Credentials</AlertTitle>
                  Turning on Temporary Authorization and Pending Credentials allows AnswersNow to
                  see the client while authorizations and BCBA credentials are pending approvals
                </Alert>
              </Grid>
              <Grid item xs={6} sx={{ marginTop: "24px" }}>
                <ANDatePicker
                  label="Start date"
                  value={newAuthorizationStartDate}
                  onChange={(date: Date | null) => onChangeDate("newAuthorizationStartDate", date)}
                  slotProps={{
                    field: { fullWidth: true },
                    popper: { disablePortal: false, style: { zIndex: 4000 } },
                  }}
                />
              </Grid>
              <Grid item xs={6} sx={{ marginTop: "24px" }}>
                <ANDatePicker
                  label="End date"
                  value={newAuthorizationEndDate}
                  onChange={(date: Date | null) => onChangeDate("newAuthorizationEndDate", date)}
                  slotProps={{
                    field: { fullWidth: true },
                    popper: { disablePortal: false, style: { zIndex: 4000 } },
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>

        {newAuthorizationType !== "secondary_auth" && newApprovedUnits.length > 0 && (
          <>
            <Card variant="outlined" style={{ margin: "24px 0" }}>
              <Grid container style={{ backgroundColor: "#f2f2f2" }}>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={3} style={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle1" style={{ padding: "16px" }}>
                        Billing code
                      </Typography>
                    </Grid>
                    <Grid item xs={3} style={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle1" style={{ padding: "16px" }}>
                        Number of units
                      </Typography>
                    </Grid>
                    <Grid item xs={3} style={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle1" style={{ padding: "16px" }}>
                        Number of hours
                      </Typography>
                    </Grid>
                    <Grid item xs={3} style={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="subtitle1"
                        style={{ padding: "16px", paddingLeft: "32px" }}
                      >
                        Action
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {newApprovedUnits.map((unit, i) => (
                <React.Fragment key={`code-${i}`}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <TextField
                            select
                            id="type"
                            onChange={(e) => onChangeApprovedUnitCode(i, e)}
                            value={unit.code}
                            fullWidth
                            style={{ padding: "16px 0 16px 16px" }}
                            disabled={!newAuthorizationType}
                          >
                            <MenuItem disabled value="">
                              Select a code
                            </MenuItem>
                            {authCodes.map((code: any, index: number) => (
                              <MenuItem key={index} value={code.insurance_code}>
                                {code.insurance_code}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            id="outlined-helperText"
                            disabled={!unit.code}
                            placeholder="Enter number of units"
                            value={unit.units}
                            type="number"
                            fullWidth
                            style={{ padding: "16px 0 16px 16px" }}
                            InputProps={{ inputProps: { min: 0 } }}
                            onChange={(e) => onChangeApprovedUnitAmount(i, e)}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            id="outlined-helperText"
                            disabled
                            style={{ padding: "16px 0 16px 16px" }}
                            placeholder="Enter number of hours"
                            value={unit.hours}
                            type="number"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Box style={{ padding: "16px 0 16px 16px" }}>
                            <Button
                              color="error"
                              variant="text"
                              onClick={() => onRemoveApprovedUnit(i)}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Divider />
                </React.Fragment>
              ))}
            </Card>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          {newAuthorizationType !== "secondary_auth" ? (
            <Button
              onClick={onAddApprovedUnit}
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              disabled={!newAuthorizationType}
            >
              Add billing code
            </Button>
          ) : (
            <div />
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={onToggleNewAuthorization} variant="outlined" color="inherit">
              Cancel
            </Button>
            <Button disabled={saveAuthDisabled} onClick={saveNewAuthorization} variant="contained">
              Add Authorization Period
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NewAuthorizationDialog;
